import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import type { ConditionModel } from '../../../../typings'
import { useTranslation } from 'react-i18next'

export function useHandlers() {
  const { setFieldValue } = useFormikContext<ConditionModel>()
  const getId = useCallback((option: any) => option.id, [])
  const { t } = useTranslation()

  const handleChange = useCallback(
    (values: any) => {
      setFieldValue('assignmentRuleConditionValues', values)
    },
    [setFieldValue],
  )

  const renderSummary = useCallback(
    (options, attribute: string) => {
      if (options.length === 1) {
        switch (attribute) {
          case 'Branch':
            return options[0].name
          case 'Campaign':
          case 'LeadSource':
          case 'LocationType':
          case 'Division':
          case 'TargetPest':
            return options[0].name + ' - ' + options[0].description
          default:
            return options[0].label || options[0].name || options[0].fullName
        }
      } else {
        switch (attribute) {
          case 'Branch':
            return `${options.length} Branches`
          case 'ZipCode':
            return `${options.length} ${t('postalCode')}s`
          case 'Campaign':
            return `${options.length} Campaigns`
          case 'LeadSource':
            return `${options.length} Lead Sources`
          case 'State':
            return `${options.length} ${t('state')}s`
          case 'LocationType':
            return `${options.length} Location Types`
          case 'Division':
            return `${options.length} Divisions`
          case 'CreatedBy':
            return `${options.length} Users`
          case 'TargetPest':
            return `${options.length} Target Pests`
          case 'LeadType':
            return `${options.length} Types`
          default:
            return ''
        }
      }
    },
    [t],
  )

  const isOptionEqual = useCallback(
    (optionA: any, optionB: any) => optionA?.id === optionB?.id,
    [],
  )

  const getLabel = useCallback((option: any) => {
    if (option.description) {
      return (
        option.name + ' - ' + option.description ||
        option.fullName + ' - ' + option.description
      )
    }
    if (option.label) {
      return option.label
    }
    if (!option.label) {
      if (option?.abbreviation) {
        return (
          option.name + ` (${option?.abbreviation})` ||
          option.fullName + ` (${option?.abbreviation})`
        )
      }
      return option.name || option.fullName
    }
  }, [])

  const getOptionDisabled = useCallback((option: any) => option?.disabled, [])

  return {
    getId,
    handleChange,
    renderSummary,
    isOptionEqual,
    getLabel,
    getOptionDisabled,
  }
}
